import * as turf from '@turf/turf'
import { GeoJSON } from '../GeoJSON/index'
import { MINIMUM_PADDOCK_AREA } from '../constants'
import { normalizePaddockName } from './normalizePaddockName'

// Client-side staging support for the ID 5441 partial KML import: imported lotes are held and
// edited locally, so overlap detection and clipping must run here, against the session's working
// set — and must only ever alter the STAGED features. This is the deliberate inverse of
// mapDrawHandle's checkIntersectionWithOtherFeatures, which clips the EXISTING features.

// Mirrors ruuts-api's partial-import defaults (DEFAULT_MIN_OVERLAP_AREA_HA): overlaps at or below
// this are classified disjoint server-side, so flagging them client-side would block confirmation
// on conflicts the backend would never report.
const MIN_OVERLAP_AREA_HECTARES = 0.01

export type StagedFeatureProperties = {
  stagedId: string
  name: string
  area: number
}

export type StagedFeature = GeoJSON.Feature<
  GeoJSON.Polygon | GeoJSON.MultiPolygon,
  StagedFeatureProperties
>

// GeoJSON.hectareArea (the UTM-projected mirror of ruuts-api's GeoJSON/area.js) rather than
// turf.area: the backend validates MINIMUM_PADDOCK_AREA and the overlap threshold against
// UTM-projected planar area, so measuring with spherical WGS84 here lets near-threshold residuals
// pass client-side and then abort the whole atomic import batch server-side.
const hectares = (feature: GeoJSON.Feature): number => GeoJSON.hectareArea(feature)

// Stateless id generation keeps this module a pure utility (src/utils contract) — the
// Math.random fallback only exists for environments without WebCrypto.
const newStagedId = (): string =>
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? `staged-${crypto.randomUUID()}`
    : `staged-${Math.random().toString(36).slice(2)}`

export const toStagedFeatures = (features: GeoJSON.Feature[]): StagedFeature[] =>
  features
    .filter(
      (feature): feature is GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> =>
        feature?.geometry?.type === 'Polygon' || feature?.geometry?.type === 'MultiPolygon',
    )
    .map(feature => ({
      type: 'Feature' as const,
      geometry: feature.geometry,
      properties: {
        stagedId: newStagedId(),
        // Normalized at the source so the reviewed name, the generated-KML upload, the skip diff
        // and the row the backend persists are all the same string.
        name:
          typeof feature.properties?.name === 'string'
            ? normalizePaddockName(feature.properties.name)
            : '',
        area: hectares(feature),
      },
    }))

// The GeoJSON.intersect/difference wrappers truncate coordinates IN PLACE (turf.truncate with
// mutate: true), so both sides are cloned before any operation — staged features must stay
// byte-identical until an explicit clip, and existing paddocks must never be altered at all.
// The cast exists because turf.clone rejects GeometryCollection features, which the polygonal
// working set this receives never contains.
const cloneFeature = (feature: GeoJSON.Feature): GeoJSON.Feature =>
  turf.clone(feature as turf.AllGeoJSON) as GeoJSON.Feature

// Existing features are cloned ONCE per sweep, not once per (staged x existing) pair: turf.truncate
// is idempotent, so an already-truncated clone is safe to reuse, and the pairwise clone otherwise
// doubles the cost of an operation that is already n x m.
const overlapHectares = (staged: StagedFeature, existingClone: GeoJSON.Feature): number => {
  const intersection = GeoJSON.intersect(cloneFeature(staged), existingClone)
  return intersection ? hectares(intersection) : 0
}

// Returns stagedIds (not names) so callers can cache the geometric result — an n x m intersect
// sweep — while still deriving fresh names after a rename.
export const findConflictingStagedFeatureIds = ({
  stagedFeatures,
  existingFeatures,
}: {
  stagedFeatures: StagedFeature[]
  existingFeatures: GeoJSON.Feature[]
}): string[] => {
  const existingClones = existingFeatures.map(cloneFeature)
  return stagedFeatures
    .filter(staged =>
      existingClones.some(
        existing => overlapHectares(staged, existing) > MIN_OVERLAP_AREA_HECTARES,
      ),
    )
    .map(staged => staged.properties.stagedId)
}

export const clipStagedFeaturesAgainstExisting = ({
  stagedFeatures,
  existingFeatures,
  minimumFeatureHectares = MINIMUM_PADDOCK_AREA,
}: {
  stagedFeatures: StagedFeature[]
  existingFeatures: GeoJSON.Feature[]
  minimumFeatureHectares?: number
}): { clippedFeatures: StagedFeature[]; removedNames: string[] } => {
  const clippedFeatures: StagedFeature[] = []
  const removedNames: string[] = []
  const existingClones = existingFeatures.map(cloneFeature)

  stagedFeatures.forEach(staged => {
    const residual = existingClones.reduce<GeoJSON.Feature | null>(
      (remaining, existing) => (remaining ? GeoJSON.difference(remaining, existing) : null),
      cloneFeature(staged),
    )

    // Sub-minimum residuals are removed alongside empty ones: the backend rejects them at
    // confirmation anyway ("Size too small"), which would abort the whole atomic batch.
    if (!residual || hectares(residual) < minimumFeatureHectares) {
      removedNames.push(staged.properties.name)
      return
    }

    clippedFeatures.push({
      ...staged,
      geometry: residual.geometry as GeoJSON.Polygon | GeoJSON.MultiPolygon,
      properties: { ...staged.properties, area: hectares(residual) },
    })
  })

  return { clippedFeatures, removedNames }
}
